import * as L from 'leaflet';
import { FeatureDisplayItemGenerator } from '../../featureDisplay/featureDisplayItemGenerator';
import { MapLayer } from '../mapLayer.abstract';
import { GeoJsonObject, Feature, GeometryObject, Geometry } from 'geojson';
import { FeatureDisplayItem } from '../../featureDisplay/featureDisplayItem';

import * as turf from '@turf/turf';

export class GeoJsonLayerFeatureItemGenerator implements FeatureDisplayItemGenerator {
  public jsonFeatureIdentifier = true; // used for type evaluation
  protected fixedClickBufferPx = 3;
  protected clickBufferPxFunc: (feature: Feature) => number;

  constructor(
    protected mapLayer: MapLayer,
    protected geoJsonData: GeoJsonObject,
    protected featureDisplayContentFunc: (
      feature: Feature<GeometryObject, Record<string, unknown>>,
    ) => string,
    protected featureContentClickFunc: (
      event: MouseEvent,
      feature: Feature<GeometryObject, Record<string, unknown>>,
    ) => void,
  ) {}

  public setFixedZoomBufferPx(pxCount: number): void {
    this.fixedClickBufferPx = pxCount;
  }
  public setClickBufferPxFunc(clickBufferPxFunc: (feature: Feature) => number): void {
    this.clickBufferPxFunc = clickBufferPxFunc;
  }

  public getFeatureDisplayItems(
    clickEvent: L.LeafletMouseEvent,
  ): Promise<Array<FeatureDisplayItem>> {
    // console.debug('getFeatureDisplayItems', clickEvent);
    return new Promise((resolve) => {
      const leafletObj = this.mapLayer.getBgsLeaflet().leafletMapObj;
      // clicking on a circlemarker sets the click point to the feature lat lng, so use the original event
      const latLngActualClickPoint = leafletObj.mouseEventToLatLng(clickEvent.originalEvent);

      const featureItemsMap = new Map<number, FeatureDisplayItem>();
      if (this.geoJsonData != null) {
        const fixedClickBufferMeters = this.getZoomBufferSize(
          clickEvent.latlng,
          this.fixedClickBufferPx,
        );
        turf.flattenEach(
          this.geoJsonData as Feature<Geometry, Record<string, unknown>>,
          (feature: Feature<Geometry, Record<string, unknown>>, featureIndex: number) => {
            if (!featureItemsMap.has(featureIndex)) {
              const clickBufferMeters =
                null != this.clickBufferPxFunc
                  ? this.getZoomBufferSize(latLngActualClickPoint, this.clickBufferPxFunc(feature))
                  : fixedClickBufferMeters;

              if (this.pointInFeature(feature, latLngActualClickPoint, clickBufferMeters)) {
                // const actualClickPoint = leafletObj.layerPointToContainerPoint(clickEvent.latlng);
                featureItemsMap.set(
                  featureIndex,
                  new FeatureDisplayItem(
                    feature,
                    () => this.featureDisplayContentFunc(feature),
                    (event: MouseEvent) => this.featureContentClickFunc(event, feature),
                  ),
                );
              }
            }
          },
        );
      }
      // console.debug('getLayerPopupItem', mapPopupItems);
      resolve(Array.from(featureItemsMap.values()));
    });
  }

  protected pointInFeature(feature: Feature, point: L.LatLng, metersBuffer: number): boolean {
    // console.debug('pointInFeature', feature, point, metersBuffer);
    const turfClickPoint = turf.point([point.lng, point.lat]);
    /* eslint-disable no-useless-assignment */
    let returnVal = false;
    const bufferedFeature = turf.buffer(feature, metersBuffer, { units: 'meters' });
    returnVal = turf.booleanPointInPolygon(turfClickPoint, bufferedFeature);
    return returnVal;
  }

  // returns how much we will buffer features by for deciding if clicked, in meters.
  // Especially needed for lineStrings
  protected getZoomBufferSize(latLng: L.LatLng, bufferPx: number): number {
    const leafletMapObj = this.mapLayer.getBgsLeaflet().leafletMapObj;
    const clickPoint = leafletMapObj.latLngToContainerPoint(latLng);

    // latLng px up and right
    const refLatLng = leafletMapObj.containerPointToLatLng(
      L.point(clickPoint.x + bufferPx, clickPoint.y + bufferPx),
    );

    return latLng.distanceTo(refLatLng);
  }
}
